import { Button } from "@mui/material";
import { GridRowSelectionModel } from "@mui/x-data-grid";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useState } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import Flex from "../../../../components/Flex";
import Text from "../../../../components/Text";
import CustomTabs from "../../../../components/app/CustomTabs";
import StyledButtonV2 from "../../../../components/app/StyledButtonV2";
// import { useRoleController } from "../../../../components/useRoleController";
// import { useReduceDisclosure } from "../../../../hooks/use-reduce-disclosure";
import { useGetPatientPortalEmails } from "@/queries/patient/use-get-all-email";
import DeleteModal from "../../../../components/app/DeleteModal";
import useCurrentUser from "../../../../hooks/local-storage/use-current-user";
import useDisclosure from "../../../../hooks/use-disclosure";
import { useReduceDisclosure } from "../../../../hooks/use-reduce-disclosure";
import { useSnackbar } from "../../../../hooks/useSnackbar";
import {
  ContactDictionaryFilters,
  SmsTextFilterState,
  SmsTextFilters,
} from "../../../../pages/app/provider/communications/components/FilterComponents";
import {
  CONTACT_DICTIONARY_FILTER_INITIAL_VALUES,
  ContactDictionaryFilter,
} from "../../../../pages/app/provider/communications/contact-dictionary/contact-dictionary-constant";
import { usePatientPortalEmailArchive } from "../../../../queries/patient/use-patient-portal-email-archive";
import { usePatientPortalEmailUnreadCount } from "../../../../queries/patient/use-patient-portal-email-unread-count";
import useFcmChatEventListener from "../../../../services/fcm/listeners/FcmChatEventListener";
import {
  DEF_PAGINATION,
  DGT_Pagination,
  getSuccessSnackbar,
  getXtenantId,
  roleFilter,
} from "../../../../utils/utils";
import PatientSectionWrapper from "../PatientSectionWrapper";
import PatientSendBulkEmailModal from "./email/PatientSendBulkEmailModal";

// --- SMS/Chat/Dictionary imports commented out (not used in patient portal currently) ---
// import { useCallback } from "react";
// import {
//   ContactDictionaryFilters,
//   SmsTextFilterState,
//   SmsTextFilters,
// } from "../../../../pages/app/provider/communications/components/FilterComponents";
// import {
//   CONTACT_DICTIONARY_FILTER_INITIAL_VALUES,
//   ContactDictionaryFilter,
// } from "../../../../pages/app/provider/communications/contact-dictionary/contact-dictionary-constant";
// import useFcmChatEventListener from "../../../../services/fcm/listeners/FcmChatEventListener";

const PatientPortalCommunicationLayout = () => {
  const [patient] = useCurrentUser();
  const patientId = patient?.uuid;
  const [activeTab, setActiveTab] = useState<string>(() => {
    const currentTab = location.pathname.split("/").pop();
    return currentTab == "communication" ? "chat" : (currentTab as string);
  });

  const navigate = useNavigate();
  // const { hasView: hasViewChat } = useRoleController("Communication > Chat");
  // const { hasView: hasViewEmail } = useRoleController("Communication > eMail");
  // const { hasView: hasViewSms } = useRoleController("Communication > SMS");

  // --- SMS filter states commented out (not used in patient portal currently) ---
  const [smsFilters, setSmsFilters] = useState<SmsTextFilterState>(
    CONTACT_DICTIONARY_FILTER_INITIAL_VALUES.sms
  );
  const [contactDictionaryFilters, setContactDictionaryFilters] =
    useState<ContactDictionaryFilter>(
      CONTACT_DICTIONARY_FILTER_INITIAL_VALUES.contact
    );

  // --- Generic filter/search handlers commented out (not used in patient portal currently) ---
  const handleFilterChange = useCallback(
    (filterType: string, key: string, value: any) => {
      const setterMap = {
        sms: setSmsFilters,
        dictionary: setContactDictionaryFilters,
      };
      const setter = setterMap[filterType as keyof typeof setterMap];
      if (setter) {
        setter((prev: any) => ({ ...prev, [key]: value }));
      }
    },
    []
  );

  const handleSearch = useCallback((filterType: string, query: string) => {
    const setterMap = {
      sms: setSmsFilters,
      dictionary: setContactDictionaryFilters,
    };
    const setter = setterMap[filterType as keyof typeof setterMap];
    if (setter) {
      setter((prev: any) => ({
        ...prev,
        searchQuery: query,
        archive: filterType === "dictionary" ? false : prev.archive,
      }));
    }
  }, []);

  const handleSmsFilterChange = useCallback(
    (key: string, value: string) => {
      handleFilterChange("sms", key, value);
    },
    [handleFilterChange]
  );

  const handleContactDictionaryFilterChange = useCallback(
    (key: string, value: { value: string; label: string }) => {
      handleFilterChange("dictionary", key, value);
    },
    [handleFilterChange]
  );

  const handleSmsSearch = useCallback(
    (query: string) => {
      handleSearch("sms", query);
    },
    [handleSearch]
  );

  const handleContactDictionarySearch = useCallback(
    (query: string) => {
      handleSearch("dictionary", query);
    },
    [handleSearch]
  );

  // --- contactDictionaryDisclosure commented out (not used in patient portal currently) ---
  const [contactDictionaryDisclosure] = useReduceDisclosure(2);
  const queryClient = useQueryClient();
  const sendMailDisclosure = useDisclosure(false);
  const bulkDeleteDisclosure = useDisclosure(false);
  const showSnackbar = useSnackbar();
  const { mutateAsync: archiveEmail } = usePatientPortalEmailArchive();
  const { data: unreadEmailCount, refetch: refetchUnreadCount } =
    usePatientPortalEmailUnreadCount({
      patientId: patientId || "",
    });

  const [rowSelectionModel, setRowSelectionModel] =
    useState<GridRowSelectionModel>([]);
  const [pagination, setPagination] = useState<DGT_Pagination>(DEF_PAGINATION);
  const {
    data: patientEmailList,
    isLoading: patientEmailListLoading,
    isRefetching: patientEmailListRefetching,
  } = useGetPatientPortalEmails({
    patientId: patientId || "",
    pagination,
    xTENANTID: getXtenantId(),
    enabled: activeTab === "email",
    archive: false,
  });

  const handleSendMail = () => {
    const filteredPatients = patientEmailList?.data?.content
      ?.filter((it: any) => rowSelectionModel?.includes(it?.uuid))
      .map((it: any) => ({
        uuid: it?.uuid,
        name: `${it?.firstName || ""}  ${it?.lastName || ""}`,
      }));

    sendMailDisclosure.open(filteredPatients);
  };

  const handleBulkDelete = () => {
    bulkDeleteDisclosure.open(rowSelectionModel as string[]);
  };

  async function bulkArchiveHandler(archiveStatus: boolean, ids: any) {
    return archiveEmail({
      emailUuids: ids as string[],
      archiveStatus,
    }).then((res) => {
      queryClient.invalidateQueries({ queryKey: ["patient-portal-emails"] });
      queryClient.invalidateQueries({
        queryKey: ["patient-portal-email-unread-count"],
      });
      showSnackbar(getSuccessSnackbar(res));
      setRowSelectionModel([]);
      return res;
    });
  }

  // Function to render the appropriate filter component based on active tab
  const renderFilters = () => {
    switch (activeTab) {
      // --- SMS filters commented out (not used in patient portal currently) ---
      case "":
        return (
          <SmsTextFilters
            filters={smsFilters}
            onFilterChange={handleSmsFilterChange}
            onSearch={handleSmsSearch}
          />
        );
      case "email":
        return (
          <Flex width={"100%"} justifyContent={"flex-end"}>
            <Flex gap={2}>
              {rowSelectionModel?.length ? (
                <>
                  <Flex gap={2} alignItems={"center"}>
                    <Text>{`${rowSelectionModel.length} Selected`}</Text>
                  </Flex>
                  <Button
                    variant="outlined"
                    sx={{
                      width: "80px",
                    }}
                    onClick={() => setRowSelectionModel([])}
                  >
                    Clear
                  </Button>
                  <StyledButtonV2
                    variant="outlined"
                    onClick={handleBulkDelete}
                    label="Bulk Delete"
                  />
                  <Button
                    variant="contained"
                    sx={{ width: "148px" }}
                    onClick={handleSendMail}
                  >
                    Send Emails
                  </Button>
                </>
              ) : (
                <StyledButtonV2
                  variant="contained"
                  onClick={() => sendMailDisclosure.open()}
                  shouldAddIcon
                  label="Compose Email"
                />
              )}
            </Flex>
            <PatientSendBulkEmailModal
              disClosureHost={sendMailDisclosure}
              onSave={() => setRowSelectionModel([])}
            />
            <DeleteModal
              w="380px"
              open={bulkDeleteDisclosure.opened}
              onClose={bulkDeleteDisclosure.close}
              id={bulkDeleteDisclosure.data}
              onConfirmation={(ids) => bulkArchiveHandler(true, ids)}
              content={`You want to delete ${(bulkDeleteDisclosure.data as string[])?.length || 0} selected email(s)`}
            />
          </Flex>
        );
      // --- Dictionary filters commented out (not used in patient portal currently) ---
      case "dictionary":
        return (
          <ContactDictionaryFilters
            filters={contactDictionaryFilters}
            onFilterChange={handleContactDictionaryFilterChange as any}
            onSearch={handleContactDictionarySearch}
            contactDictionaryDisclosure={contactDictionaryDisclosure}
          />
        );
      default:
        return null;
    }
  };

  const [chatCount, setChatFcmCount] = useFcmChatEventListener({});

  const COMMUNICATION_TABS_DATA = [
    // {
    //   value: "",
    //   label: "SMS/Text",
    // },
    {
      value: "chat",
      label: "Chat",
      count: chatCount,
    },
    {
      value: "email",
      label: "Email",
      count: unreadEmailCount ?? 0,
    },
  ];

  return (
    <PatientSectionWrapper
      header={
        <Flex alignItems={"center"} width={"100%"}>
          {/* Tabs and Filter Bar Combined */}
          <Flex width={"100%"}>
            <CustomTabs
              tabsData={roleFilter({
                list: COMMUNICATION_TABS_DATA,
                compWith: {
                  // chat: hasViewChat,
                  // email: hasViewEmail,
                  // sms: hasViewSms,
                },
              })}
              value={activeTab}
              onChange={(v) => {
                setActiveTab(v);
                navigate(v);
              }}
            />
          </Flex>
        </Flex>
      }
      endAction={renderFilters()}
    >
      <Outlet
        context={{
          setChatFcmCount,
          // --- SMS related data commented out (not used in patient portal currently) ---
          smsFilters,
          handleSmsFilterChange,
          handleSmsSearch,
          // --- Contact dictionary related data commented out (not used in patient portal currently) ---
          contactDictionaryFilters,
          handleContactDictionaryFilterChange,
          handleContactDictionarySearch,
          contactDictionaryDisclosure,
          // email related data are passed to EmailTable
          sendMailDisclosure,
          rowSelectionModel,
          setRowSelectionModel,
          pagination,
          setPagination,
          patientEmailList,
          patientEmailListLoading,
          patientEmailListRefetching,
          refetchUnreadCount,
        }}
      />
    </PatientSectionWrapper>
  );
};

export default PatientPortalCommunicationLayout;

// export const TABS_DATA = ;

export const COMMUNICATION_LAYOUT_HEIGHT = "calc(100vh - 9rem)";
